import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Text, Grid, Heading, Button } from '@simplywallst/ui-core';

import { useFlag } from '@/features/unleash';

import { getStaticUrl, useCloudflareImage } from '@/hooks/useCloudflareImage';

import { ROUTE_PORTFOLIO_NEW } from '@/constants/routes';

import { BannerWrapper, ImageWrapper, NewPortfolioLink } from './styled';
import { SWS_HOST } from '@/constants';

const MigrateLegacyPortfolioBanner = loadable(
  () => import('./MigrateLegacyPortfolioBanner'),
  {
    resolveComponent: (c) => c.MigrateLegacyPortfolioBanner,
  }
);

interface Props {
  v1PortfolioId: string | null;
  holdingCount: number;
}

const isPortfolioIdValid = (id: string | null) =>
  id !== null && isNaN(Number(id)) === false;

export const DeprecatedNewPortfolioBanner = ({
  v1PortfolioId,
  holdingCount,
}: Props) => {
  const flag = useFlag({
    name: 'mono-portfolio-migration',
  });
  const hasAccessToPortfolioMigration =
    flag.enabled && holdingCount < Number(flag.variant.payload?.value);

  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'banner',
  });

  const progressTrackerImg = useCloudflareImage({
    src: getStaticUrl('portfolio/feature-usp/progress-tracker.png'),
    width: 127,
    height: 127,
  });

  if (!flag.flagsReady) {
    return null;
  }

  if (hasAccessToPortfolioMigration && isPortfolioIdValid(v1PortfolioId)) {
    return (
      <MigrateLegacyPortfolioBanner v1PortfolioId={Number(v1PortfolioId)} />
    );
  }

  return (
    <BannerWrapper
      data-cy-id="portfolio-new-banner"
      gapMd="6px"
      templateColumnsMd="80% auto"
    >
      <Grid rowGap="8px" justifyItems="start">
        <Heading as="h3" typography="baseStrong">
          Introducing Our New Portfolio
        </Heading>
        <Text typography="xSmall">
          Exciting news! Our new portfolio version is here, offering a revamped
          monitoring experience. While we develop portfolio analysis, support to
          transactions and more, you can continue using this legacy version.
          Start building your new portfolio today or wait until we seamlessly
          transfer your existing portfolio.{' '}
          <Button
            as="a"
            styleType="strongLink"
            href={`${SWS_HOST}/help/articles/hc/en-us/articles/7461937035279-Building-the-new-portfolio-tool-Roadmap-`}
            target="_blank"
          >
            Learn more
          </Button>
        </Text>
        <NewPortfolioLink
          to={{ type: ROUTE_PORTFOLIO_NEW }}
          onClick={() => {
            trackEvent({
              action: 'click',
              modifier: 'betaTestNewPortfolio',
            });
          }}
          styleType="louderLarge"
          data-cy-id="portfolio-new-banner-link"
        >
          Create New Portfolio
        </NewPortfolioLink>
      </Grid>
      <ImageWrapper alignContent="center" justifyContent="center">
        <img alt="Track portfolio's progress" src={progressTrackerImg} />
      </ImageWrapper>
    </BannerWrapper>
  );
};
